/**
 * 数据预取工具
 * 在用户可能需要之前预先加载数据，提升用户体验
 */

import apiOptimizer from './api-optimizer'
import { productAPI, enterpriseAPI, thinktankAPI, eventAPI, expertCategoriesAPI, authAPI, favoritesAPI } from './api'

class DataPrefetcher {
  constructor() {
    this.prefetchedPages = new Set()
    this.prefetchQueue = []
    this.isProcessing = false
  }

  /**
   * 预取首页数据
   */
  prefetchHomeData() {
    const prefetchKey = 'home_data'

    if (this.prefetchedPages.has(prefetchKey)) {
      return
    }

    this.prefetchedPages.add(prefetchKey)

    // 预取热门产品（低优先级）
    apiOptimizer.preload('home_hot_products', () =>
      productAPI.getProducts({ page: 1, limit: 8, featured: true }), 'low'
    )

    // 预取推荐企业
    apiOptimizer.preload('home_featured_enterprises', () =>
      enterpriseAPI.getEnterprises({ page: 1, limit: 6, featured: true }), 'low'
    )

    // 预取最新动态
    apiOptimizer.preload('home_recent_events', () =>
      eventAPI.getEvents({ page: 1, limit: 5 }), 'medium'
    )
  }

  /**
   * 预取产品列表数据
   */
  prefetchProductList() {
    const prefetchKey = 'product_list'

    if (this.prefetchedPages.has(prefetchKey)) {
      return
    }

    this.prefetchedPages.add(prefetchKey)

    // 预取产品分类
    apiOptimizer.preload('product_categories', () =>
      productAPI.getCategories(), 'high'
    )

    // 预取第一页产品
    apiOptimizer.preload('product_list_page_1', () =>
      productAPI.getProducts({ page: 1, limit: 12 }), 'high'
    )

    // 预取热门产品
    apiOptimizer.preload('hot_products', () =>
      productAPI.getProducts({ page: 1, limit: 6, sort: 'hot' }), 'medium'
    )
  }

  /**
   * 预取企业列表数据
   */
  prefetchEnterpriseList() {
    const prefetchKey = 'enterprise_list'

    if (this.prefetchedPages.has(prefetchKey)) {
      return
    }

    this.prefetchedPages.add(prefetchKey)

    // 预取第一页企业
    apiOptimizer.preload('enterprise_list_page_1', () =>
      enterpriseAPI.getEnterprises({ page: 1, limit: 12 }), 'high'
    )
  }

  /**
   * 预取专家智库数据
   */
  prefetchExpertList() {
    const prefetchKey = 'expert_list'

    if (this.prefetchedPages.has(prefetchKey)) {
      return
    }

    this.prefetchedPages.add(prefetchKey)

    // 预取专家分类
    apiOptimizer.preload('expert_categories', () =>
      expertCategoriesAPI.getExpertCategories(), 'high'
    )

    // 预取第一页专家
    apiOptimizer.preload('expert_list_page_1', () =>
      thinktankAPI.getExperts({ page: 1, limit: 12 }), 'high'
    )
  }

  /**
   * 预取用户相关数据（登录后）
   */
  prefetchUserData() {
    const prefetchKey = 'user_data'

    if (this.prefetchedPages.has(prefetchKey)) {
      return
    }

    this.prefetchedPages.add(prefetchKey)

    // 预取用户资料
    apiOptimizer.preload('user_profile', () =>
      authAPI.getUserInfo(), 'high'
    )

    // 预取用户收藏
    apiOptimizer.preload('user_favorites', () =>
      favoritesAPI.getFavorites({ page: 1, limit: 6 }), 'low'
    )
  }

  /**
   * 基于路由预取数据
   */
  prefetchByRoute(route) {
    switch (route.name) {
      case 'Home':
      case 'YJSYIndex':
        this.prefetchHomeData()
        break

      case 'ProductList':
        this.prefetchProductList()
        // 同时预取相关企业数据
        this.prefetchEnterpriseList()
        break

      case 'EmergencyEnterprises':
        this.prefetchEnterpriseList()
        break

      case 'EmergencyThinktank':
        this.prefetchExpertList()
        break

      default:
        // 对于其他页面，可以预取一些通用数据
        this.prefetchCommonData()
    }
  }

  /**
   * 预取通用数据
   */
  prefetchCommonData() {
    // 预取用户状态（如果已登录）
    if (localStorage.getItem('token')) {
      apiOptimizer.preload('user_basic_info', () =>
        authAPI.getUserInfo(), 'medium'
      )
    }
  }

  /**
   * 智能预取（基于用户行为预测）
   */
  intelligentPrefetch(currentRoute, userActions = []) {
    const predictions = this._predictUserIntent(currentRoute, userActions)

    predictions.forEach(prediction => {
      switch (prediction.type) {
        case 'products':
          this.prefetchProductList()
          break
        case 'enterprises':
          this.prefetchEnterpriseList()
          break
        case 'experts':
          this.prefetchExpertList()
          break
        case 'user':
          this.prefetchUserData()
          break
      }
    })
  }

  /**
   * 预测用户意图
   */
  _predictUserIntent(currentRoute, userActions) {
    const predictions = []

    // 基于当前页面预测
    switch (currentRoute.name) {
      case 'Home':
        predictions.push({ type: 'products', confidence: 0.8 })
        predictions.push({ type: 'enterprises', confidence: 0.6 })
        break

      case 'ProductList':
        predictions.push({ type: 'enterprises', confidence: 0.7 })
        break

      case 'EmergencyEnterprises':
        predictions.push({ type: 'products', confidence: 0.7 })
        break
    }

    // 基于用户行为预测
    userActions.forEach(action => {
      switch (action.type) {
        case 'search':
          predictions.push({ type: 'products', confidence: 0.9 })
          predictions.push({ type: 'enterprises', confidence: 0.8 })
          break
        case 'view_product':
          predictions.push({ type: 'enterprises', confidence: 0.8 })
          break
        case 'view_enterprise':
          predictions.push({ type: 'products', confidence: 0.8 })
          break
      }
    })

    // 按置信度排序，取前3个
    return predictions
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 3)
  }

  /**
   * 预取资源（图片、字体等）
   */
  prefetchResources(urls) {
    urls.forEach(url => {
      const link = document.createElement('link')
      link.rel = 'prefetch'
      link.href = url
      document.head.appendChild(link)
    })
  }

  /**
   * 清理预取缓存
   */
  clearPrefetchCache() {
    this.prefetchedPages.clear()
    this.prefetchQueue.length = 0
  }

  /**
   * 获取预取状态
   */
  getPrefetchStatus() {
    return {
      prefetchedPages: Array.from(this.prefetchedPages),
      queueLength: this.prefetchQueue.length,
      isProcessing: this.isProcessing
    }
  }
}

// 创建全局实例
const dataPrefetcher = new DataPrefetcher()

export default dataPrefetcher